// @givmo/auth — session persistence (`rememberMe`).
//
// Ported from the webapp admin `signInWithEmailAndPassword`:
//   if (rememberMe) setPersistence(Persistence.LOCAL); else setPersistence(Persistence.SESSION);
// The web SDK equivalent swaps `Persistence.LOCAL`/`SESSION` for the singleton persistence objects
// `browserLocalPersistence` / `browserSessionPersistence`.
//
// `local`  → survives tab close + browser restart (the "remember me" intent).
// `session` → cleared when the tab/window closes (the not-remembered intent).
//
// ORDERING — this TS uses the canonical before-sign-in ordering: apply persistence BEFORE the
// `signIn*` call so the resulting session is stored at the chosen scope (Firebase's `setPersistence`
// is itself async and must resolve first). The webapp Dart set persistence AFTER sign-in — a known
// footgun, since the first session can land at the wrong (default) scope before the persistence
// switch takes effect — which this canonical ordering corrects.

import {
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  type Auth,
} from 'firebase/auth';
import type { SessionPersistence } from './types';

/** Map the `rememberMe` boolean to the persistence enum. `true` → `local`, `false` → `session`. */
export function persistenceForRememberMe(rememberMe: boolean): SessionPersistence {
  return rememberMe ? 'local' : 'session';
}

/**
 * Set the Firebase Auth persistence scope for subsequent sign-ins. Call BEFORE `signIn*`. Resolves
 * the corresponding `browserLocalPersistence` / `browserSessionPersistence` singleton.
 */
export async function applySessionPersistence(
  auth: Auth,
  persistence: SessionPersistence,
): Promise<void> {
  await setPersistence(
    auth,
    persistence === 'local' ? browserLocalPersistence : browserSessionPersistence,
  );
}

/** Convenience: apply persistence directly from a `rememberMe` flag. */
export function applyRememberMe(auth: Auth, rememberMe: boolean): Promise<void> {
  return applySessionPersistence(auth, persistenceForRememberMe(rememberMe));
}
